/**
 * Centralized date display utilities for Viveka super-admin.
 *
 * CONVENTION:
 *  - All dates arrive from the API in ISO 8601 UTC format
 *  - Display formatting is locale-aware using Intl.DateTimeFormat
 *  - User timezone is applied at the display layer only
 */

export type DateStyle = 'us' | 'eu' | 'iso' | 'legal'

/**
  * Format a UTC ISO date string for display.
  *
  * @example
  *   formatDate('2026-03-23T14:00:00.000Z', 'us')    // "Mar 23, 2026"
  *   formatDate('2026-03-23T14:00:00.000Z', 'legal') // "March 23, 2026"
  */
export function formatDate(
   isoString: string | null | undefined,
   style: DateStyle = 'us',
   userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
 ): string {
   if (!isoString) return ''
   const date = new Date(isoString)
   if (isNaN(date.getTime())) return ''

  switch (style) {
   case 'us':
          return new Intl.DateTimeFormat('en-US', {
                   month: 'short', day: 'numeric', year: 'numeric',
                   timeZone: userTimezone,
          }).format(date)
   case 'eu':
          return new Intl.DateTimeFormat('en-GB', {
                   day: 'numeric', month: 'short', year: 'numeric',
                   timeZone: userTimezone,
          }).format(date)
   case 'iso':
          return isoString.slice(0, 10)
   case 'legal':
          return new Intl.DateTimeFormat('en-US', {
                   month: 'long', day: 'numeric', year: 'numeric',
                   timeZone: userTimezone,
          }).format(date)
   default:
          return new Intl.DateTimeFormat('en-US', {
                   month: 'short', day: 'numeric', year: 'numeric',
                   timeZone: userTimezone,
          }).format(date)
  }
}

/**
 * Format a UTC ISO date string with time for display.
 *
 * @example
 *   formatDateTime('2026-03-23T14:00:00.000Z') // "Monday, March 23, 2026, 2:00:00 PM"
 */
export function formatDateTime(
   isoString: string | null | undefined,
   userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
 ): string {
   if (!isoString) return ''
   const date = new Date(isoString)
   if (isNaN(date.getTime())) return ''

  return new Intl.DateTimeFormat('en-US', {
       weekday: 'long',
       month: 'long',
       day: 'numeric',
       year: 'numeric',
       hour: 'numeric',
       minute: '2-digit',
       second: '2-digit',
       hour12: true,
       timeZone: userTimezone,
  }).format(date)
}

/**
 * Get a human-friendly relative time string (e.g., "2 hours ago", "in 3 days").
 */
export function timeAgo(isoString: string | null | undefined): string {
   if (!isoString) return ''
   const date = new Date(isoString)
   if (isNaN(date.getTime())) return ''

  const now = Date.now()
   const diffMs = now - date.getTime()
   const diffSec = Math.floor(Math.abs(diffMs) / 1000)
   const past = diffMs >= 0

  if (diffSec < 60) return past ? 'just now' : 'in a moment'

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
       ['year', 31536000],
       ['month', 2592000],
       ['week', 604800],
       ['day', 86400],
       ['hour', 3600],
       ['minute', 60],
     ]

  for (const [unit, seconds] of units) {
       if (diffSec >= seconds) {
              const value = Math.floor(diffSec / seconds)
              return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
                       past ? -value : value,
                       unit,
                     )
       }
  }

  return 'just now'
}
